import { LlmFilesDropzone } from '../LlmFilesDropzone';
import { useId, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useDataProvider, useNotify, useTranslate } from 'ra-core';
import { type AdminDataProvider } from '../../../../api/adminDataProvider';
import { Button } from '../../../../components/ui/button';
import { Textarea } from '../../../../components/ui/textarea';
import { Card, CardContent } from '../../../../components/ui/card';
import { Separator } from '../../../../components/ui/separator';
import { Loader2 } from 'lucide-react';

export function PromptTest() {
  const [files, setFiles] = useState<File[]>([]);
  const [userPrompt, setUserPrompt] = useState<string>('');
  const [model, setModel] = useState<string>('gemini-3.7-flash');
  const [systemPrompt, setSystemPrompt] = useState<string>('');
  const [llmResponse, setLlmResponse] = useState<string>('');

  const dataProvider = useDataProvider<AdminDataProvider>();
  const notify = useNotify();
  const translate = useTranslate();
  const modelId = useId();
  const systemPromptId = useId();
  const userPromptId = useId();

  const sendPrompt = useMutation({
    mutationFn: async () => {
      setLlmResponse('');

      // 1. Upload files first
      const fileUrls = await Promise.all(
        files.map((file) =>
          dataProvider.uploadFile(
            {
              rawFile: file,
              title: file.name,
            },
            'public'
          )
        )
      );

      // 2. Call the new experimental streaming chat messages endpoint
      const stream =
        await dataProvider.postUnitInsightLlmExperimentalChatMessage({
          message: userPrompt,
          model,
          systemPrompt,
          fileUrls,
        });

      for await (const chunk of stream) {
        if (chunk.type === 'error') {
          throw new Error(
            typeof chunk.error === 'string'
              ? chunk.error
              : translate('moonstar.unitPerformance.unknownError')
          );
        }

        const text = chunk.data.data.text;
        setLlmResponse((prev) => prev + text);
      }
    },
    onError: (error) => {
      notify(
        error instanceof Error
          ? error.message
          : translate('moonstar.unitPerformance.unknownError'),
        { type: 'error' }
      );
    },
  });

  const handleNewChat = () => {
    setLlmResponse('');
    setFiles([]);
    notify(translate('moonstar.unitPerformance.promptTest.newChatStarted'), {
      type: 'info',
    });
  };

  return (
    <div className="flex h-[calc(100vh-120px)] min-h-0 flex-col overflow-hidden">
      {/* 1. Header (Sticky/Fixed on top) */}
      <div className="flex flex-wrap items-center justify-between gap-4 border-b bg-card p-4">
        <div>
          <h1 className="text-xl font-bold">
            {translate('moonstar.unitPerformance.promptTest.title')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {translate('moonstar.unitPerformance.promptTest.description')}
          </p>
        </div>
        <Button variant="outline" onClick={handleNewChat}>
          {translate('moonstar.unitPerformance.promptTest.newChat')}
        </Button>
      </div>

      {/* 2. Response Area (Scrollable) */}
      <div className="min-h-0 flex-1 overflow-y-auto bg-background p-3">
        {llmResponse ? (
          <Card>
            <CardContent>
              <h2 className="text-lg font-semibold">
                {translate('moonstar.unitPerformance.promptTest.response')}
              </h2>
              <Separator />
              <p className="whitespace-pre-wrap">{llmResponse}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="flex h-full items-center justify-center text-muted-foreground">
            <p>
              {translate('moonstar.unitPerformance.promptTest.emptyResponse')}
            </p>
          </div>
        )}
      </div>

      {/* 3. Input Area (Fixed at bottom) */}
      <div className="border-t bg-card p-3 shadow-md">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!sendPrompt.isPending) {
              sendPrompt.mutate();
            }
          }}
        >
          <div className="space-y-4">
            <div className="grid gap-4 lg:grid-cols-3">
              <div className="grid gap-2">
                <label className="text-sm font-medium" htmlFor={modelId}>
                  {translate('moonstar.unitPerformance.promptTest.model')}
                </label>
                <Textarea
                  id={modelId}
                  rows={3}
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  placeholder={translate(
                    'moonstar.unitPerformance.promptTest.modelPlaceholder'
                  )}
                />
                <a
                  className="text-sm text-primary underline-offset-4 hover:underline"
                  href="https://ai.google.dev/gemini-api/docs/models"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {translate('moonstar.unitPerformance.promptTest.viewModels')}
                </a>
              </div>
              <label
                className="grid gap-2 text-sm font-medium"
                htmlFor={systemPromptId}
              >
                {translate('moonstar.unitPerformance.promptTest.systemPrompt')}
                <Textarea
                  id={systemPromptId}
                  rows={3}
                  value={systemPrompt}
                  onChange={(e) => setSystemPrompt(e.target.value)}
                  placeholder={translate(
                    'moonstar.unitPerformance.promptTest.systemPromptPlaceholder'
                  )}
                />
              </label>
              <label
                className="grid gap-2 text-sm font-medium"
                htmlFor={userPromptId}
              >
                {translate('moonstar.unitPerformance.promptTest.userPrompt')}
                <Textarea
                  id={userPromptId}
                  rows={3}
                  required
                  value={userPrompt}
                  onChange={(e) => setUserPrompt(e.target.value)}
                  placeholder={translate(
                    'moonstar.unitPerformance.promptTest.userPromptPlaceholder'
                  )}
                />
              </label>
            </div>

            <div className="flex flex-col items-stretch gap-4 md:flex-row md:items-center">
              <div className="min-w-0 flex-1">
                <LlmFilesDropzone
                  files={files}
                  onFilesChange={setFiles}
                  disabled={sendPrompt.isPending}
                />
              </div>

              <Button
                type="submit"
                variant="default"
                size="lg"
                disabled={!userPrompt.trim()}
              >
                {sendPrompt.isPending ? (
                  <Loader2 className="animate-spin" />
                ) : null}
                {translate('moonstar.unitPerformance.chat.send')}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
